'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, Globe, Loader2, Lock, Receipt, Send, SquarePen, Edit3, Trash2, X } from 'lucide-react';
import { authService } from '@/lib/authService';
import { useAuth } from '@/hooks/useAuth';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { useTheme } from '@/contexts/ThemeContext';
import { confirm, notify } from '@/lib/notifications';
import { UserAvatar, UserIdentityLink, getUserDisplayName } from '@/components/work-items/WorkItemUserIdentity';
import {
  FormattedCommentBody,
  getCommentSourceBadgeMeta,
  extractMentionUserIdsFromCommentText,
  type CommentSourceReferenceKind,
} from '@/components/work-items/commentPresentation';
import { useWorkItemCommentMentions } from '@/components/work-items/useWorkItemCommentMentions';
import { isTotalFinanceWorkItemComment, type WorkItemCommentType } from '@/lib/work-items/commentTypes';
import { hasPrivilegedCommentRole } from '@/lib/work-items/commentAccess';
import { isCapacitor } from '@/lib/platformUtils';

const getAuthToken = (): string => authService.getTokens()?.access_token || '';
const TOTAL_FINANCE_COMMENT_COMPOSER_OVERLAY_Z_CLASS = 'z-[200]';

interface TotalFinanceCommentRow {
  id: string;
  work_item_id: string;
  user_id: number;
  user_name: string;
  user_email?: string;
  user_display_name?: string;
  comment_text: string;
  is_internal?: boolean;
  comment_type: WorkItemCommentType;
  created_at: string;
  updated_at: string;
  source_appointment_id?: string | null;
  source_appointment_number?: string | null;
  source_reference_kind?: CommentSourceReferenceKind | null;
}

export interface TotalFinanceCommentsSectionProps {
  workItemId: string;
  isActive: boolean;
  includeRelated?: boolean;
  /** @deprecated Rows use API permissions + ownership; retained for callers. */
  listReadOnly?: boolean;
  readOnly?: boolean;
  /** Tighter spacing when embedded under timers / billing / expenses. */
  embedded?: boolean;
  mentionTeamId?: string | null;
  enableMentions?: boolean;
  /** Optional line item ID for line-item-specific comments */
  lineItemId?: string | null;
  /** Context type for comment filtering (e.g., 'document', 'line_item') */
  context?: string;
  /** Callback when a comment is added */
  onCommentAdded?: () => void;
  /** Callback with the current total finance comment count for this scope */
  onCommentsChange?: (count: number) => void;
}

function formatCommentDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

async function parseJsonResponse<T>(response: Response): Promise<T | null> {
  const text = await response.text().catch(() => '');
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return null;
  }
}

export default function TotalFinanceCommentsSection({
  workItemId,
  isActive,
  includeRelated = false,
  listReadOnly: _listReadOnly = false,
  readOnly = false,
  embedded = false,
  mentionTeamId = null,
  enableMentions = true,
  lineItemId = null,
  context,
  onCommentAdded,
  onCommentsChange,
}: TotalFinanceCommentsSectionProps) {
  void _listReadOnly;
  const { user, isRoot } = useAuth();
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const currentUserId = user?.id != null ? Number(user.id) : null;
  const clientId = user?.client_id ? String(user.client_id) : null;

  const [comments, setComments] = useState<TotalFinanceCommentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingComment, setEditingComment] = useState<TotalFinanceCommentRow | null>(null);
  const [isInternal, setIsInternal] = useState(true);
  const [togglingVisibility, setTogglingVisibility] = useState<string | null>(null);
  const [apiCanModerate, setApiCanModerate] = useState(false);
  const loadRequestIdRef = useRef(0);
  const realtimeRefreshTimeoutRef = useRef<number | null>(null);
  const canCompose = !readOnly;
  const canModerateComments = apiCanModerate || hasPrivilegedCommentRole(user) || isRoot;

  const {
    textareaRef,
    mentionMenuOpen,
    mentionMembersLoading,
    filteredMentionSuggestions,
    mentionHighlight,
    mentionCandidates,
    handleComposerChange,
    handleComposerCaretMove,
    applyMentionPick,
    handleMentionKeyDown,
    ingestMentionTeamFromCommentsApi,
  } = useWorkItemCommentMentions({
    workItemId,
    mentionTeamIdProp: mentionTeamId,
    enableMentions,
    currentUserId,
    getAuthToken,
    mentionUiEnabled: canCompose && composerOpen,
    draft,
    setDraft,
  });

  const loadComments = useCallback(async () => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setLoading(true);

    try {
      const params = new URLSearchParams();
      if (includeRelated) params.append('include_related', 'true');
      if (lineItemId) params.append('line_item_id', lineItemId);
      const queryString = params.toString();
      const response = await fetch(`/api/v1/work-items/${workItemId}/comments${queryString ? `?${queryString}` : ''}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (loadRequestIdRef.current !== requestId) {
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to load comments');
      }

      const data = await parseJsonResponse<{
        success?: boolean;
        data?: TotalFinanceCommentRow[];
        mention_team_id?: string | null;
        can_moderate_comments?: boolean;
      }>(response);

      if (!data?.success || !Array.isArray(data.data)) {
        setComments([]);
        setApiCanModerate(false);
        ingestMentionTeamFromCommentsApi(null);
        return;
      }

      setApiCanModerate(Boolean(data.can_moderate_comments));
      ingestMentionTeamFromCommentsApi(
        typeof data.mention_team_id === 'string' && data.mention_team_id.trim().length > 0
          ? data.mention_team_id.trim()
          : null,
      );

      const totalFinanceOnly = data.data
        .filter((row) => isTotalFinanceWorkItemComment(row))
        .map((row) => ({
          ...row,
          comment_type: row.comment_type || 'total_finance',
        }))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setComments(totalFinanceOnly);
      onCommentsChange?.(totalFinanceOnly.length);
    } catch (e) {
      console.error('[TotalFinanceCommentsSection] load failed', e);
      notify.error('Could not load Total Finance comments');
      setComments([]);
      setApiCanModerate(false);
      ingestMentionTeamFromCommentsApi(null);
    } finally {
      if (loadRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [includeRelated, workItemId, lineItemId, ingestMentionTeamFromCommentsApi, onCommentsChange]);

  useEffect(() => {
    setComments([]);
    setDraft('');
    setEditingComment(null);
    setComposerOpen(false);
    setIsInternal(true);
  }, [workItemId]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    void loadComments();
  }, [isActive, loadComments, workItemId]);

  useEffect(() => {
    if (!composerOpen) {
      return;
    }
    const t = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [composerOpen, editingComment, textareaRef]);

  const scheduleRealtimeRefresh = useCallback(() => {
    if (!isActive) {
      return;
    }

    if (typeof window === 'undefined') {
      void loadComments();
      return;
    }

    if (realtimeRefreshTimeoutRef.current) {
      window.clearTimeout(realtimeRefreshTimeoutRef.current);
    }

    realtimeRefreshTimeoutRef.current = window.setTimeout(() => {
      realtimeRefreshTimeoutRef.current = null;
      void loadComments();
    }, 120);
  }, [isActive, loadComments]);

  useWebSocketChannel({
    channel: clientId ? `operations:client:${clientId}` : undefined,
    autoConnect: true,
    onMessage: (data) => {
      const message = (data || {}) as Record<string, unknown>;
      const type = typeof message.type === 'string' ? message.type : '';
      if (type === 'operations_refresh' && message.source === 'work_item_comments') {
        const payload = message.payload as Record<string, unknown> | undefined;
        if (payload?.work_item_id === workItemId) {
          scheduleRealtimeRefresh();
        }
      }
    },
  });

  const handleSubmit = useCallback(async () => {
    const trimmedDraft = draft.trim();
    if (!trimmedDraft) {
      return;
    }

    setSubmitting(true);
    try {
      const mentionedUserIds = extractMentionUserIdsFromCommentText(trimmedDraft);
      const payload = {
        comment_text: trimmedDraft,
        is_internal: isInternal,
        comment_type: 'total_finance' as const,
        mentioned_user_ids: mentionedUserIds,
        line_item_id: lineItemId,
        context,
      };

      const response = await fetch(`/api/v1/work-items/${workItemId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await parseJsonResponse<{ success?: boolean; error?: string }>(response);
        throw new Error(errorData?.error || 'Failed to post comment');
      }

      setDraft('');
      setComposerOpen(false);
      void loadComments();
      onCommentAdded?.();
      notify.success('Comment posted');
    } catch (e) {
      console.error('[TotalFinanceCommentsSection] post failed', e);
      notify.error('Could not post comment');
    } finally {
      setSubmitting(false);
    }
  }, [draft, workItemId, lineItemId, context, isInternal, loadComments, onCommentAdded]);

  const handleEdit = useCallback(async () => {
    if (!editingComment) {
      return;
    }

    const trimmedDraft = draft.trim();
    if (!trimmedDraft) {
      return;
    }

    setSubmitting(true);
    try {
      const mentionedUserIds = extractMentionUserIdsFromCommentText(trimmedDraft);
      const payload = {
        comment_text: trimmedDraft,
        is_internal: isInternal,
        mentioned_user_ids: mentionedUserIds,
      };

      const response = await fetch(
        `/api/v1/work-items/${workItemId}/comments?comment_id=${editingComment.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await parseJsonResponse<{ success?: boolean; error?: string }>(response);
        throw new Error(errorData?.error || 'Failed to update comment');
      }

      setDraft('');
      setEditingComment(null);
      setComposerOpen(false);
      void loadComments();
      notify.success('Comment updated');
    } catch (e) {
      console.error('[TotalFinanceCommentsSection] edit failed', e);
      notify.error('Could not update comment');
    } finally {
      setSubmitting(false);
    }
  }, [draft, editingComment, workItemId, isInternal, loadComments]);

  const handleDelete = useCallback(
    async (commentId: string) => {
      await confirm({
        title: 'Delete Total Finance comment',
        message: 'Remove this Total Finance comment? This cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        variant: 'danger',
        onConfirm: async () => {
          try {
            const response = await fetch(
              `/api/v1/work-items/${workItemId}/comments?comment_id=${commentId}`,
              {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${getAuthToken()}` },
              },
            );

            if (!response.ok) {
              const errorData = await parseJsonResponse<{ success?: boolean; error?: string }>(response);
              throw new Error(errorData?.error || 'Failed to delete comment');
            }

            void loadComments();
            notify.success('Comment deleted');
          } catch (e) {
            console.error('[TotalFinanceCommentsSection] delete failed', e);
            notify.error('Could not delete comment');
          }
        },
      });
    },
    [workItemId, loadComments],
  );

  const handleToggleVisibility = useCallback(
    async (comment: TotalFinanceCommentRow) => {
      if (togglingVisibility === comment.id) return;
      setTogglingVisibility(comment.id);
      try {
        const response = await fetch(
          `/api/v1/work-items/${workItemId}/comments?comment_id=${comment.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({
              is_internal: !comment.is_internal,
            }),
          },
        );

        if (!response.ok) {
          const errorData = await parseJsonResponse<{ success?: boolean; error?: string }>(response);
          throw new Error(errorData?.error || 'Failed to update comment');
        }

        void loadComments();
        notify.success(comment.is_internal ? 'Comment is now visible on PDFs' : 'Comment is now internal');
      } catch (e) {
        console.error('[TotalFinanceCommentsSection] toggle visibility failed', e);
        notify.error('Could not update comment visibility');
      } finally {
        setTogglingVisibility(null);
      }
    },
    [workItemId, loadComments, togglingVisibility],
  );

  const startEdit = useCallback(
    (comment: TotalFinanceCommentRow) => {
      setEditingComment(comment);
      setDraft(comment.comment_text);
      setIsInternal(comment.is_internal ?? true);
      setComposerOpen(true);
    },
    [],
  );

  const cancelEdit = useCallback(() => {
    setEditingComment(null);
    setDraft('');
    setIsInternal(true);
    setComposerOpen(false);
  }, []);

  const openComposer = useCallback(() => {
    setIsInternal(true);
    setComposerOpen(true);
  }, []);

  const closeComposer = useCallback(() => {
    if (editingComment) {
      cancelEdit();
    } else {
      setComposerOpen(false);
      setDraft('');
      setIsInternal(true);
    }
  }, [editingComment, cancelEdit]);

  if (!isActive) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Total Finance Comments
        </h3>
        {canCompose && !composerOpen && (
          <button
            type="button"
            onClick={openComposer}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
            Add Comment
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 text-gray-400 dark:text-gray-500 animate-spin" />
        </div>
      )}

      {!loading && comments.length === 0 && !composerOpen && (
        <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
          No Total Finance comments yet
        </div>
      )}

      <div className="flex flex-col gap-2">
        {comments.map((comment) => (
          <div
            key={comment.id}
            className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <UserAvatar
                  user={{
                    userId: comment.user_id,
                    displayName: comment.user_display_name || comment.user_name,
                    email: comment.user_email,
                  }}
                  size="sm"
                />
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
                    {getUserDisplayName({
                      userId: comment.user_id,
                      displayName: comment.user_display_name || comment.user_name,
                      email: comment.user_email,
                    })}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatCommentDateTime(comment.created_at)}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium w-fit ${
                      comment.is_internal
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                    }`}
                  >
                    {comment.is_internal ? <Lock className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                    {comment.is_internal ? 'Internal' : 'Visible on PDF'}
                  </span>
                </div>
              </div>
              {(canModerateComments || currentUserId === comment.user_id) && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleToggleVisibility(comment)}
                    disabled={togglingVisibility === comment.id}
                    className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-50"
                    title={comment.is_internal ? 'Change to visible on PDF' : 'Change to internal'}
                  >
                    {comment.is_internal ? <Globe className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(comment)}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Edit comment"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(comment.id)}
                    className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    title="Delete comment"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
              <FormattedCommentBody text={comment.comment_text} />
            </div>
          </div>
        ))}
      </div>

      {composerOpen && canCompose && (
        <div className="relative">
          <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={handleComposerChange}
              onSelect={handleComposerCaretMove}
              onKeyUp={handleComposerCaretMove}
              onKeyDown={handleMentionKeyDown}
              placeholder={editingComment ? 'Edit your comment...' : 'Add a Total Finance comment...'}
              className="w-full min-h-[80px] p-2 text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-900/50 rounded-md border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              disabled={submitting}
            />

            {mentionMenuOpen && (
              <div className="absolute z-50 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-lg mt-1">
                {mentionMembersLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                  </div>
                ) : filteredMentionSuggestions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    No matching users
                  </div>
                ) : (
                  filteredMentionSuggestions.map((suggestion, idx) => (
                    <button
                      key={suggestion.user_id}
                      type="button"
                      onClick={() => applyMentionPick(suggestion)}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                        idx === mentionHighlight
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-100'
                          : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      {suggestion.label}
                    </button>
                  ))
                )}
              </div>
            )}

            <div className="flex items-center justify-between gap-2 mt-2">
              <button
                type="button"
                onClick={() => setIsInternal(!isInternal)}
                disabled={submitting}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-medium rounded-md transition-colors disabled:opacity-50 ${
                  isInternal
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/50'
                }`}
                title={isInternal ? 'Internal comments are not printed on PDFs' : 'Public comments are printed on PDFs'}
              >
                {isInternal ? <Lock className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
                {isInternal ? 'Internal (not on PDF)' : 'Public (print on PDF)'}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeComposer}
                  disabled={submitting}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={editingComment ? handleEdit : handleSubmit}
                  disabled={submitting || !draft.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      {editingComment ? 'Updating...' : 'Posting...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      {editingComment ? 'Update' : 'Post'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
